// ─── Email Service ─────────────────────────────────────────────────────────────
// Calls the Netlify serverless function which proxies to Resend.
// The Resend API key never touches the browser — it lives only in
// the Netlify Function environment variable (RESEND_API_KEY).

import { supabase } from "@/lib/supabase/client";

const FUNCTION_URL = "/.netlify/functions/send-email";

async function sendEmail(
  toEmail: string,
  subject: string,
  html: string,
): Promise<void> {
  const { data: { session } } = await supabase.auth.getSession();
  const response = await fetch(FUNCTION_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "Authorization": `Bearer ${session?.access_token ?? ""}`,
    },
    body: JSON.stringify({ toEmail, subject, html }),
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(`Email send failed (${response.status}): ${text}`);
  }
}

// ─── Templates ────────────────────────────────────────────────────────────────

export async function sendWelcomeEmail(toEmail: string): Promise<void> {
  await sendEmail(
    toEmail,
    "Welcome to Momentum Peaks",
    `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <title>Welcome to Momentum Peaks</title>
</head>
<body style="margin:0;padding:0;background:#f1f5f9;font-family:'Segoe UI',system-ui,-apple-system,sans-serif;">

  <table width="100%" cellpadding="0" cellspacing="0" border="0" style="background:#f1f5f9;padding:40px 16px;">
    <tr>
      <td align="center">

        <table width="100%" cellpadding="0" cellspacing="0" border="0" style="max-width:560px;background:#ffffff;border-radius:16px;border:1px solid #e2e8f0;overflow:hidden;">

          <!-- Accent bar -->
          <tr>
            <td style="height:4px;background:linear-gradient(90deg,#1b6b9d 0%,#5d92b8 100%);font-size:0;line-height:0;">&nbsp;</td>
          </tr>

          <!-- Header -->
          <tr>
            <td style="padding:32px 40px 24px;">
              <span style="font-size:16px;font-weight:800;letter-spacing:0.08em;color:#0f172a;text-transform:uppercase;">MOMENTUM PEAKS</span>
            </td>
          </tr>

          <!-- Divider -->
          <tr>
            <td style="padding:0 40px;"><div style="height:1px;background:#e2e8f0;">&nbsp;</div></td>
          </tr>

          <!-- Body -->
          <tr>
            <td style="padding:36px 40px 32px;">

              <h1 style="font-size:26px;font-weight:700;color:#0f172a;margin:0 0 16px;line-height:1.25;">
                Your training starts now.
              </h1>

              <p style="font-size:15px;color:#475569;line-height:1.7;margin:0 0 12px;">
                Welcome aboard! Your account is ready.
              </p>

              <p style="font-size:15px;color:#475569;line-height:1.7;margin:0 0 32px;">
                Complete your profile to get your first personalised training week — tailored to your FTP, goals, and schedule.
              </p>

              <!-- CTA -->
              <table cellpadding="0" cellspacing="0" border="0" style="margin-bottom:32px;">
                <tr>
                  <td style="border-radius:10px;background:#1b6b9d;">
                    <a
                      href="https://momentumpeaks.com/onboarding"
                      style="display:inline-block;padding:15px 36px;font-size:15px;font-weight:700;color:#ffffff;text-decoration:none;letter-spacing:0.02em;border-radius:10px;"
                    >
                      Set Up My Profile →
                    </a>
                  </td>
                </tr>
              </table>

            </td>
          </tr>

          <!-- Divider -->
          <tr>
            <td style="padding:0 40px;"><div style="height:1px;background:#e2e8f0;">&nbsp;</div></td>
          </tr>

          <!-- Footer -->
          <tr>
            <td style="padding:24px 40px 32px;">
              <p style="font-size:12px;color:#94a3b8;line-height:1.7;margin:0 0 4px;">
                You're receiving this because you created an account at momentumpeaks.com.
              </p>
              <p style="font-size:12px;color:#cbd5e1;margin:0;">
                © ${new Date().getFullYear()} Momentum Peaks ·
                <a href="https://momentumpeaks.com" style="color:#94a3b8;text-decoration:none;">momentumpeaks.com</a>
              </p>
            </td>
          </tr>

        </table>

      </td>
    </tr>
  </table>

</body>
</html>`,
  );
}

export async function sendPasswordResetEmail(
  toEmail: string,
  resetUrl: string,
): Promise<void> {
  await sendEmail(
    toEmail,
    "Reset your Momentum Peaks password",
    `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <title>Reset your password</title>
</head>
<body style="margin:0;padding:0;background:#f1f5f9;font-family:'Segoe UI',system-ui,-apple-system,sans-serif;">

  <table width="100%" cellpadding="0" cellspacing="0" border="0" style="background:#f1f5f9;padding:40px 16px;">
    <tr>
      <td align="center">

        <table width="100%" cellpadding="0" cellspacing="0" border="0" style="max-width:560px;background:#ffffff;border-radius:16px;border:1px solid #e2e8f0;overflow:hidden;">

          <!-- Accent bar -->
          <tr>
            <td style="height:4px;background:linear-gradient(90deg,#1b6b9d 0%,#5d92b8 100%);font-size:0;line-height:0;">&nbsp;</td>
          </tr>

          <!-- Header -->
          <tr>
            <td style="padding:32px 40px 24px;">
              <span style="font-size:16px;font-weight:800;letter-spacing:0.08em;color:#0f172a;text-transform:uppercase;">MOMENTUM PEAKS</span>
            </td>
          </tr>

          <!-- Divider -->
          <tr>
            <td style="padding:0 40px;"><div style="height:1px;background:#e2e8f0;">&nbsp;</div></td>
          </tr>

          <!-- Body -->
          <tr>
            <td style="padding:36px 40px 32px;">

              <h1 style="font-size:26px;font-weight:700;color:#0f172a;margin:0 0 16px;line-height:1.25;">
                Reset your password
              </h1>

              <p style="font-size:15px;color:#475569;line-height:1.7;margin:0 0 8px;">
                We received a request to reset the password for your Momentum Peaks account associated with
                <span style="color:#0f172a;font-weight:600;">${toEmail}</span>.
              </p>

              <p style="font-size:15px;color:#475569;line-height:1.7;margin:0 0 32px;">
                Click the button below to choose a new password. This link is valid for <strong style="color:#0f172a;">1 hour</strong>.
              </p>

              <!-- CTA -->
              <table cellpadding="0" cellspacing="0" border="0" style="margin-bottom:32px;">
                <tr>
                  <td style="border-radius:10px;background:#1b6b9d;">
                    <a
                      href="${resetUrl}"
                      style="display:inline-block;padding:15px 36px;font-size:15px;font-weight:700;color:#ffffff;text-decoration:none;letter-spacing:0.02em;border-radius:10px;"
                    >
                      Set New Password →
                    </a>
                  </td>
                </tr>
              </table>

              <!-- Fallback URL -->
              <p style="font-size:12px;color:#94a3b8;line-height:1.6;margin:0 0 4px;">
                Button not working? Copy and paste this link into your browser:
              </p>
              <p style="font-size:12px;margin:0;word-break:break-all;">
                <a href="${resetUrl}" style="color:#1b6b9d;text-decoration:underline;">${resetUrl}</a>
              </p>

            </td>
          </tr>

          <!-- Divider -->
          <tr>
            <td style="padding:0 40px;"><div style="height:1px;background:#e2e8f0;">&nbsp;</div></td>
          </tr>

          <!-- Footer -->
          <tr>
            <td style="padding:24px 40px 32px;">
              <p style="font-size:12px;color:#94a3b8;line-height:1.7;margin:0 0 4px;">
                If you didn't request a password reset, you can safely ignore this email — your password won't change.
              </p>
              <p style="font-size:12px;color:#cbd5e1;margin:0;">
                © ${new Date().getFullYear()} Momentum Peaks ·
                <a href="https://momentumpeaks.com" style="color:#94a3b8;text-decoration:none;">momentumpeaks.com</a>
              </p>
            </td>
          </tr>

        </table>

      </td>
    </tr>
  </table>

</body>
</html>`,
  );
}
